import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * useFlexLsp - Hook for WebSocket LSP connection
 *
 * Features:
 * - WebSocket connection management
 * - LSP message handling
 * - Diagnostics updates
 * - Reconnection logic
 */
function useFlexLsp(documentUri = 'flex://untitled') {
  const [connected, setConnected] = useState(false);
  const [diagnostics, setDiagnostics] = useState([]);
  const [error, setError] = useState(null);

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const messageIdRef = useRef(1);
  const pendingRequestsRef = useRef(new Map());
  const currentDocRef = useRef({ uri: documentUri, text: '' });

  // Get next message ID
  const getNextId = useCallback(() => {
    return messageIdRef.current++;
  }, []);

  // Send LSP message
  const sendMessage = useCallback((message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      return true;
    }
    return false;
  }, []);

  // Send request and wait for response
  const sendRequest = useCallback((method, params) => {
    return new Promise((resolve, reject) => {
      const id = getNextId();
      const message = {
        jsonrpc: '2.0',
        id,
        method,
        params,
      };

      pendingRequestsRef.current.set(id, { resolve, reject });

      if (!sendMessage(message)) {
        pendingRequestsRef.current.delete(id);
        reject(new Error('WebSocket not connected'));
      }

      // Timeout after 5 seconds
      setTimeout(() => {
        if (pendingRequestsRef.current.has(id)) {
          pendingRequestsRef.current.delete(id);
          reject(new Error('Request timeout'));
        }
      }, 5000);
    });
  }, [getNextId, sendMessage]);

  // Send notification (no response expected)
  const sendNotification = useCallback((method, params) => {
    const message = {
      jsonrpc: '2.0',
      method,
      params,
    };
    return sendMessage(message);
  }, [sendMessage]);

  // Handle incoming messages
  const handleMessage = useCallback((event) => {
    try {
      const message = JSON.parse(event.data);

      // Handle response to a request
      if (message.id !== undefined && pendingRequestsRef.current.has(message.id)) {
        const { resolve, reject } = pendingRequestsRef.current.get(message.id);
        pendingRequestsRef.current.delete(message.id);

        if (message.error) {
          reject(new Error(message.error.message));
        } else {
          resolve(message.result);
        }
        return;
      }

      // Handle notification
      if (message.method === 'textDocument/publishDiagnostics') {
        const params = message.params;
        if (params.uri === currentDocRef.current.uri) {
          // Convert LSP diagnostics to our format
          const newDiagnostics = (params.diagnostics || []).map((diag) => ({
            severity: diag.severity === 1 ? 'error' : diag.severity === 2 ? 'warning' : 'info',
            message: diag.message,
            line: (diag.range?.start?.line || 0) + 1,  // Convert to 1-based
            column: (diag.range?.start?.character || 0) + 1,
            endLine: (diag.range?.end?.line || 0) + 1,
            endColumn: (diag.range?.end?.character || 0) + 1,
          }));
          setDiagnostics(newDiagnostics);
        }
      }
    } catch (err) {
      console.error('Error parsing LSP message:', err);
    }
  }, []);

  // Connect to LSP server
  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    // Determine WebSocket URL
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const wsUrl = `${protocol}//${host}/ws/flex-lsp`;

    try {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('FLEX LSP connected');
        setConnected(true);
        setError(null);

        // Send initialize request
        sendRequest('initialize', {
          processId: null,
          capabilities: {},
          rootUri: null,
        }).then(() => {
          // Send initialized notification
          sendNotification('initialized', {});

          // Re-open current document if we have one
          if (currentDocRef.current.text) {
            sendNotification('textDocument/didOpen', {
              textDocument: {
                uri: currentDocRef.current.uri,
                languageId: 'flex',
                version: 1,
                text: currentDocRef.current.text,
              },
            });
          }
        }).catch((err) => {
          console.error('LSP initialization failed:', err);
        });
      };

      ws.onmessage = handleMessage;

      ws.onerror = (err) => {
        console.error('FLEX LSP error:', err);
        setError('WebSocket connection error');
      };

      ws.onclose = (event) => {
        console.log('FLEX LSP disconnected:', event.code, event.reason);
        setConnected(false);
        wsRef.current = null;

        // Attempt reconnection after delay
        if (!event.wasClean) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('Attempting LSP reconnection...');
            connect();
          }, 3000);
        }
      };

      wsRef.current = ws;
    } catch (err) {
      console.error('Failed to create WebSocket:', err);
      setError('Failed to connect to LSP server');
    }
  }, [handleMessage, sendRequest, sendNotification]);

  // Disconnect from LSP server
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      // Send shutdown and exit
      sendRequest('shutdown', null)
        .then(() => sendNotification('exit', null))
        .catch(() => {})
        .finally(() => {
          wsRef.current?.close();
          wsRef.current = null;
        });
    }
  }, [sendRequest, sendNotification]);

  // Notify document opened
  const didOpen = useCallback((uri, text) => {
    currentDocRef.current = { uri, text };
    return sendNotification('textDocument/didOpen', {
      textDocument: {
        uri,
        languageId: 'flex',
        version: 1,
        text,
      },
    });
  }, [sendNotification]);

  // Notify document changed
  const didChange = useCallback((uri, text, version = 1) => {
    currentDocRef.current = { uri, text };
    return sendNotification('textDocument/didChange', {
      textDocument: {
        uri,
        version,
      },
      contentChanges: [{ text }],
    });
  }, [sendNotification]);

  // Notify document closed
  const didClose = useCallback((uri) => {
    return sendNotification('textDocument/didClose', {
      textDocument: { uri },
    });
  }, [sendNotification]);

  // Request completions
  const getCompletions = useCallback(async (uri, line, character) => {
    try {
      const result = await sendRequest('textDocument/completion', {
        textDocument: { uri },
        position: { line, character },
      });
      return result || [];
    } catch (err) {
      console.error('Completion request failed:', err);
      return [];
    }
  }, [sendRequest]);

  // Request hover
  const getHover = useCallback(async (uri, line, character) => {
    try {
      const result = await sendRequest('textDocument/hover', {
        textDocument: { uri },
        position: { line, character },
      });
      return result;
    } catch (err) {
      console.error('Hover request failed:', err);
      return null;
    }
  }, [sendRequest]);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  return {
    connected,
    diagnostics,
    error,
    connect,
    disconnect,
    didOpen,
    didChange,
    didClose,
    getCompletions,
    getHover,
  };
}

export default useFlexLsp;
